import { useQuery } from '@apollo/client';
import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
} from '@apollo/client';
import { createContext, useContext } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';

type ContextedQueryProviderProps<TData, TVariables extends OperationVariables> = PropsWithChildren<{
  options?: QueryHookOptions<TData, TVariables>;
}>;

type CreateContextedQueryOptions = {
  name: string;
};

/**
 * Creates one Apollo query owner and hooks that read its normal QueryResult.
 * Query options belong to the provider so every consumer shares the same observable.
 */
export function createContextedQuery<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  { name }: CreateContextedQueryOptions
) {
  const QueryContext = createContext<QueryResult<TData, TVariables> | undefined>(undefined);
  QueryContext.displayName = `${name}Context`;

  const Provider = ({
    children,
    options,
  }: ContextedQueryProviderProps<TData, TVariables>): ReactElement => {
    const result = useQuery<TData, TVariables>(query, options);

    return <QueryContext.Provider value={result}>{children}</QueryContext.Provider>;
  };

  Provider.displayName = `${name}Provider`;

  function useContextedQuery(): QueryResult<TData, TVariables> {
    const result = useContext(QueryContext);
    if (result == null) {
      throw new Error(`${name} must be read inside ${name}Provider`);
    }
    return result;
  }

  return {
    Provider,
    useQuery: useContextedQuery,
  };
}
